<template>
    <div>
        <v-menu
            v-model="menu"
            :close-on-content-click="false"
            :nudge-right="40"
            :disabled="disabled"
            transition="scale-transition"
            :allow-overflow="true"
            offset-y
        >
            <template #activator="{ props: activatorProps }">
                <v-text-field
                    v-bind="{ ...activatorProps, ...fieldProps }"
                    v-model="textValue"
                    :label="label"
                    persistent-hint
                    prepend-icon="mdi-calendar"
                    variant="outlined"
                    density="comfortable"
                    :disabled="disabled"
                    :error="hasError"
                    :error-messages="errorMessages"
                    @blur="handleBlur"
                    @input="handleInput"
                ></v-text-field>
            </template>

            <v-locale-provider locale="de">
                <v-date-picker
                    v-model="isoDate"
                    class="elevation-4"
                    show-current
                    header="keepThisToRemoveTranslationWarning"
                    hide-header
                    @update:model-value="updateDate"
                >
                    <template #actions>
                        <v-btn color="primary" size="small" @click="setDateToday">
                            {{ $t('heute') }}
                        </v-btn>
                    </template>
                </v-date-picker>
            </v-locale-provider>
        </v-menu>
    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

const props = defineProps({
    disabled: { type: Boolean, default: false },
    modelValue: { type: String, default: '' },
    label: { type: String, default: '' },
    errorMessages: { type: Array<string>, default: () => [] },
    error: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelValue', 'change', 'blur']);

const menu = ref(false);
const isoDate = ref<Date | null>(null);
const textValue = ref(props.modelValue);
const touched = ref(false);

// VeeValidate Kompatibilität
const fieldProps = computed(() => ({
    onBlur: handleBlur,
    onInput: handleInput
}));

const hasError = computed(() => {
    return props.error || (touched.value && props.errorMessages.length > 0);
});

watch(
    () => props.modelValue,
    (newVal) => {
        textValue.value = newVal;
        if (!newVal) {
            isoDate.value = null;
        }
    }
);

const handleInput = () => {
    touched.value = true;
};

const handleBlur = () => {
    touched.value = true;
    emit('blur');
    labelChanged();
};

const updateDate = (newDate: Date | null) => {
    isoDate.value = newDate;
    touched.value = true;

    if (newDate) {
        const formatted = newDate.toLocaleDateString('de-CH', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        textValue.value = formatted;
        emit('update:modelValue', formatted);
        emit('change', formatted);
    } else {
        textValue.value = '';
        emit('update:modelValue', '');
        emit('change', '');
    }

    menu.value = false;
};

const labelChanged = () => {
    const raw = textValue.value?.trim() ?? '';
    if (!raw) {
        isoDate.value = null;
        textValue.value = '';
        emit('update:modelValue', '');
        emit('change', '');
        return;
    }

    const parts = raw.split('.');
    if (parts.length === 3) {
        const [dayStr, monthStr, yearStr] = parts.map((p) => p.trim());
        const day = parseInt(dayStr, 10);
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        const newDate = new Date(year, month - 1, day);
        if (!isNaN(newDate.getTime()) && newDate.getFullYear() === year && newDate.getMonth() === month - 1 && newDate.getDate() === day) {
            const formatted = newDate.toLocaleDateString('de-CH', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
            isoDate.value = newDate;
            textValue.value = formatted;
            emit('update:modelValue', formatted);
            emit('change', formatted);
            return;
        }
    }

    // Invalid input → reset
    isoDate.value = null;
    textValue.value = '';
    emit('update:modelValue', '');
    emit('change', '');
};

const setDateToday = () => {
    updateDate(new Date());
};
</script>
